import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useMarketingLang } from "../../lib/useMarketingLang";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "../Logo";
import ChatWidget from "../ChatWidget";
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from "lucide-react";

// Delt PublicLayout for hele marketing-nettstedet. Har topp-header med
// logo/nav/språk, en scroll-baseret innramming, og en rik footer.
export default function PublicLayout({ children }) {
  const { lang, setLang, t } = useMarketingLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const location = useLocation();

  const closeAll = () => {
    setMobileOpen(false);
    setExpOpen(false);
  };

  const navItem = (to, label, testid) => (
    <NavLink
      to={to}
      end
      onClick={closeAll}
      data-testid={testid}
      className={({ isActive }) =>
        `text-sm font-medium tracking-wide transition ${
          isActive
            ? "text-[#A57F39]"
            : "text-[#0F1A2B]/80 hover:text-[#A57F39]"
        }`
      }
    >
      {label}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white font-['Manrope',system-ui,sans-serif]">
      {/* Announcement / trust strip */}
      <div className="bg-[#0F1A2B] text-white text-[11px] uppercase tracking-[0.28em] py-1.5 text-center">
        <span className="hidden sm:inline text-[#BD984D] mr-2">✦</span>
        Eco-Friendly Boat Rental in Telemark
        <span className="hidden sm:inline text-[#BD984D] ml-2">✦</span>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#A57F39]/15">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/hjem" onClick={closeAll} className="flex items-center shrink-0" data-testid="marketing-logo-link">
            <Logo size={36} withText />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {navItem("/hjem", t.nav.home, "nav-home")}
            {navItem("/flate", t.nav.fleet, "nav-fleet")}
            {/* Experiences dropdown */}
            <div className="relative" onMouseEnter={() => setExpOpen(true)} onMouseLeave={() => setExpOpen(false)}>
              <button
                type="button"
                data-testid="nav-experiences"
                className="text-sm font-medium tracking-wide text-[#0F1A2B]/80 hover:text-[#A57F39] transition inline-flex items-center gap-1"
              >
                {t.nav.experiences}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {expOpen && (
                <div className="absolute left-0 top-full pt-3 w-[240px]">
                  <div className="rounded-xl border border-[#A57F39]/25 bg-white shadow-2xl overflow-hidden">
                    <Link to="/opplevelser/kanal" onClick={closeAll} data-testid="nav-exp-canal" className="block px-4 py-3 text-sm hover:bg-[#A57F39]/10 border-b border-[#A57F39]/10">
                      {t.nav.canal}
                    </Link>
                    <Link to="/opplevelser/kyst" onClick={closeAll} data-testid="nav-exp-coast" className="block px-4 py-3 text-sm hover:bg-[#A57F39]/10 border-b border-[#A57F39]/10">
                      {t.nav.coastline}
                    </Link>
                    <Link to="/opplevelser/alt-i-ett" onClick={closeAll} data-testid="nav-exp-all" className="block px-4 py-3 text-sm hover:bg-[#A57F39]/10">
                      {t.nav.allInOne}
                    </Link>
                  </div>
                </div>
              )}
            </div>
            {navItem("/om-oss", t.nav.about, "nav-about")}
            {navItem("/faq", t.nav.faq, "nav-faq")}
            {navItem("/kontakt", t.nav.contact, "nav-contact")}
          </nav>

          <div className="flex items-center gap-2 lg:gap-3">
            <LanguageSwitcher lang={lang} setLang={setLang} />
            <Link
              to="/book"
              data-testid="nav-book-cta"
              className="hidden sm:inline-flex items-center rounded-full bg-[#A57F39] hover:bg-[#BD984D] transition text-white text-xs uppercase tracking-widest font-semibold px-4 py-2"
            >
              {t.nav.book}
            </Link>
            <button
              type="button"
              className="lg:hidden h-9 w-9 rounded-full border border-[#A57F39]/30 text-[#0F1A2B] flex items-center justify-center"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-[#A57F39]/15 bg-white" data-testid="nav-mobile-panel">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {[
                ["/hjem", t.nav.home, "m-nav-home"],
                ["/flate", t.nav.fleet, "m-nav-fleet"],
                ["/opplevelser/kanal", t.nav.canal, "m-nav-canal"],
                ["/opplevelser/kyst", t.nav.coastline, "m-nav-coast"],
                ["/opplevelser/alt-i-ett", t.nav.allInOne, "m-nav-all"],
                ["/om-oss", t.nav.about, "m-nav-about"],
                ["/faq", t.nav.faq, "m-nav-faq"],
                ["/kontakt", t.nav.contact, "m-nav-contact"],
              ].map(([to, label, tid]) => (
                <Link
                  key={to}
                  to={to}
                  onClick={closeAll}
                  data-testid={tid}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium ${
                    location.pathname === to
                      ? "bg-[#A57F39]/10 text-[#A57F39]"
                      : "text-[#0F1A2B]/85 hover:bg-[#A57F39]/5"
                  }`}
                >
                  {label}
                </Link>
              ))}
              <Link
                to="/book"
                onClick={closeAll}
                data-testid="m-nav-book"
                className="mt-2 rounded-full bg-[#A57F39] text-white text-sm uppercase tracking-widest font-semibold px-4 py-2.5 text-center"
              >
                {t.nav.book}
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0F1A2B] text-white/85 mt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="mb-4">
              <Logo size={44} withText dark />
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-md">
              {t.footer.tagline}
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#BD984D] mb-3">
              {t.footer.quickLinks}
            </div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/flate" className="hover:text-[#BD984D]">{t.nav.fleet}</Link></li>
              <li><Link to="/opplevelser/kanal" className="hover:text-[#BD984D]">{t.nav.canal}</Link></li>
              <li><Link to="/opplevelser/kyst" className="hover:text-[#BD984D]">{t.nav.coastline}</Link></li>
              <li><Link to="/faq" className="hover:text-[#BD984D]">{t.nav.faq}</Link></li>
              <li><Link to="/personvern" className="hover:text-[#BD984D]">{t.footer.privacy}</Link></li>
              <li><Link to="/login" className="hover:text-[#BD984D]">{t.nav.login}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#BD984D] mb-3">
              {t.footer.contactUs}
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-[#BD984D] mt-0.5 shrink-0" />
                {t.footer.address}
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#BD984D] shrink-0" />
                <a href={`tel:${t.footer.phone.replace(/\s/g, "")}`} className="hover:text-[#BD984D]">
                  {t.footer.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#BD984D] shrink-0" />
                <a href={`mailto:${t.footer.email}`} className="hover:text-[#BD984D]">
                  {t.footer.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/50">
          {t.footer.copyright}
        </div>
      </footer>

      {/* AI-chat widget på alle marketing-sider */}
      <ChatWidget lang={lang} page={location.pathname} />
    </div>
  );
}
